import React, { useCallback, useContext, useRef, useState } from "react";
import { useSetAtom } from "jotai";
import { AddCircleOutlined } from "@mui/icons-material";
import { customFirestore, customStorage } from "../../../firebase";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { sanitizeFilename, trackSentryException } from "../../../utils/helpers";
import { AuthContext } from "../../../auth/AuthContext";
import { customAlertStateAtom } from "../../../components/custom-alert";
import { arrayUnion, doc, updateDoc } from "@firebase/firestore";
import { Box, IconButton, LinearProgress, Tooltip } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

const UploadAudioFile = () => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const { user } = useContext<any>(AuthContext);

  const setCustomAlertState = useSetAtom(customAlertStateAtom);

  const queryClient = useQueryClient();

  const handleUploadClick = useCallback((e) => {
    e.preventDefault();
    fileInput.current?.click();
  }, []);

  const handleUpload = useCallback(
    async (selectedFile: File) => {
      if (!selectedFile) {
        alert("Please select a file");
        return;
      }
      console.log("selected file", selectedFile);
      const fileExtension =
        selectedFile.name?.toLowerCase()?.split(".").pop() ?? "";
      if (!["mp3", "wav"].includes(fileExtension)) {
        setCustomAlertState({
          open: true,
          type: "error",
          title: "Something went wrong",
          description:
            "Incorrect file type passed. Please upload a .mp3 or .wav file",
        });
        return;
      }

      setIsUploading(true);

      console.log("File extension = ", fileExtension);
      const timestamp = Math.floor(Date.now() / 1000);
      const sanitizedFilename = sanitizeFilename(selectedFile.name);
      const parsedName = `${timestamp}_${sanitizedFilename}`;

      const storageRef = ref(
        customStorage,
        `audios/${user.companyId!}/${parsedName}`
      );
      const uploadTask = uploadBytesResumable(storageRef, selectedFile);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          // Observe state change events such as progress, pause, and resume
          // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`Upload is ${progress}% done`);
          setUploadProgress(Math.round(progress));
        },
        (error) => {
          trackSentryException(error);
          console.error("Error:", error);
          setCustomAlertState({
            open: true,
            type: "error",
            title: "Something went wrong",
            description:
              "An error occurred during when uploading the audio file",
          });
          setIsUploading(false);
          setUploadProgress(0);
          // Clear the input value on error
          if (fileInput.current) {
            fileInput.current.value = "";
          }
        },
        () => {
          // Handle successful uploads on complete
          // For instance, get the download URL: https://firebasestorage.googleapis.com/...

          const companyDoc = doc(
            customFirestore,
            "companies",
            user.companyName
          );

          void getDownloadURL(uploadTask.snapshot.ref).then(
            async (downloadURL) => {
              await updateDoc(companyDoc, {
                audios: arrayUnion({
                  name: parsedName,
                  label: sanitizedFilename,
                  url: downloadURL,
                }),
              });
              await queryClient.resetQueries({
                queryKey: ["getCompanyData", user.companyId],
              });
              setCustomAlertState({
                open: true,
                type: "success",
                title: "Success",
                description: "The audio file has been uploaded successfully",
              });
              setIsUploading(false);
              setUploadProgress(0);
              // Clear the input value on success
              if (fileInput.current) {
                fileInput.current.value = "";
              }
            }
          );
        }
      );
    },
    [setCustomAlertState, user.companyId, user.companyName]
  );

  const handleFileChange = useCallback(
    (e) => {
      console.log(e);
      handleUpload(e.target.files[0]);
    },
    [handleUpload]
  );

  return (
    <>
      <input
        type="file"
        onChange={handleFileChange}
        ref={fileInput}
        style={{ display: "none" }}
      />
      <Box sx={{ alignSelf: "self-start", position: "relative" }}>
        <Tooltip title="Upload Audio">
          <IconButton
            onClick={handleUploadClick}
            loading={isUploading}
            color="primary"
            size="small"
          >
            <AddCircleOutlined />
          </IconButton>
        </Tooltip>

        {uploadProgress > 0 && (
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{
              position: "absolute",
              bottom: 2,
              left: 4,
              right: 4,
              height: 2,
              borderRadius: 1,
            }}
          />
        )}
      </Box>
    </>
  );
};
export default UploadAudioFile;
